/**
 * Experience Page Layout Component
 *
 * Dynamic page layout that renders sections based on category configuration.
 * Uses GuideListingPage layout structure with MVP 1.20 dynamic section loading.
 *
 * Epic: MVP 1.20 - Dynamic Experience Page Layout
 */

import { Suspense, useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Award, Star, MapPin, Pencil } from 'lucide-react';
import Header from '../layout/Header';
import Breadcrumb from '../layout/Breadcrumb';
import Footer from '../layout/Footer';
import ImageGallery from '../guide/ImageGallery';
import SocialProof from './sections/SocialProof';
import BookingCard from '../guide/BookingCard';
import GuideCard from '../guide/GuideCard';
import ReviewsSection from '../shared/ReviewsSection';
import ServiceInfoRenderer from '../serviceInfo/ServiceInfoRenderer';
import CancellationPolicySelector from '../guide/CancellationPolicySelector';
import BottomSheet from '../shared/BottomSheet';
import { useTheme } from '../../hooks/useTheme';
import { useBookingHold } from '../../hooks/useBookingHold';
import { useCustomerPage, useGuideEditPage } from '../../hooks/experiences/useSectionData';
import { useExperienceDetails } from '../../hooks/experiences/useExperienceDetails';
import { getSectionComponent } from '../../utils/sections/sectionRegistry';
import { formatCentsToDollars } from '../../utils/formHelpers';
import PricingDisplay from '../pricing/PricingDisplay';

/**
 * Loading Skeleton for Sections
 */
function SectionSkeleton() {
  return (
    <div className="animate-pulse bg-gray-100 rounded-lg p-8 my-4">
      <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
      <div className="space-y-3">
        <div className="h-4 bg-gray-200 rounded"></div>
        <div className="h-4 bg-gray-200 rounded w-5/6"></div>
        <div className="h-4 bg-gray-200 rounded w-4/6"></div>
      </div>
    </div>
  );
}

/**
 * Error Display Component
 */
function ErrorDisplay({ error, onRetry }) {
  return (
    <div className="bg-red-50 border border-red-200 rounded-lg p-6 my-4">
      <div className="flex items-start gap-3">
        <span className="text-2xl">❌</span>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-red-900 mb-1">
            Failed to Load Experience Page
          </h3>
          <p className="text-red-800 mb-3">
            {error || 'An unexpected error occurred'}
          </p>
          {onRetry && (
            <button
              onClick={onRetry}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Retry
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Section Renderer with Lazy Loading
 */
function SectionRenderer({ section, isGuide, categoryL1Id, experienceId, onSectionSaved, listing }) {
  // Special handling for static sections that use existing components
  // These sections are in the database for ordering, but render legacy components
  if (section.name === 'meet_your_guide' && listing?.guide) {
    return <GuideCard guide={listing.guide} />;
  }

  // Get component from registry (lazy loaded) for all other MVP 1.20 sections
  const SectionComponent = getSectionComponent(section.componentName);

  // If component is not found, skip rendering (graceful degradation)
  if (!SectionComponent) {
    console.warn(`Section component not found: ${section.componentName} (${section.name})`);
    return null;
  }

  return (
    <Suspense fallback={<SectionSkeleton />}>
      <SectionComponent
        experienceId={experienceId}
        sectionTypeId={section.sectionTypeId}
        initialData={{
          contentData: section.contentData,
          isDraft: section.isDraft,
          isPublished: section.isPublished,
          lastEditedAt: section.lastEditedAt
        }}
        isGuide={isGuide}
        categoryL1Id={categoryL1Id}
        onSectionSaved={onSectionSaved}
        listing={listing}
      />
    </Suspense>
  );
}

/**
 * Completion Stats Display (Guide Edit View Only)
 */
function _CompletionStats({ stats: _stats }) {
  if (!_stats) {return null;}

  const { totalSections, completedSections, draftSections, publishedSections, completionPercentage } = _stats;

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
      <h3 className="text-lg font-semibold text-blue-900 mb-4">Page Completion</h3>

      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex justify-between text-sm text-blue-800 mb-2">
          <span>Progress</span>
          <span className="font-semibold">{completionPercentage}%</span>
        </div>
        <div className="w-full bg-blue-200 rounded-full h-3">
          <div
            className="bg-blue-600 h-3 rounded-full transition-all duration-300"
            style={{ width: `${completionPercentage}%` }}
          />
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-4 gap-4 text-center">
        <div>
          <div className="text-2xl font-bold text-blue-900">{totalSections}</div>
          <div className="text-sm text-blue-700">Total</div>
        </div>
        <div>
          <div className="text-2xl font-bold text-green-600">{completedSections}</div>
          <div className="text-sm text-blue-700">Complete</div>
        </div>
        <div>
          <div className="text-2xl font-bold text-yellow-600">{draftSections}</div>
          <div className="text-sm text-blue-700">Drafts</div>
        </div>
        <div>
          <div className="text-2xl font-bold text-blue-600">{publishedSections}</div>
          <div className="text-sm text-blue-700">Published</div>
        </div>
      </div>
    </div>
  );
}

/**
 * Customer Experience Page (Published Sections Only)
 */
export function CustomerExperiencePage({ experienceId: _experienceId, categoryL1Id, experienceDetails: experienceDetailsFromLoader, isOwner = false, onAuthClick }) {
  const theme = useTheme();
  const navigate = useNavigate();

  // Fetch data based on ownership:
  // - Owner: Use useGuideEditPage to fetch ALL sections (including drafts)
  // - Non-owner: Use useCustomerPage to fetch only PUBLISHED sections
  // Both hooks are called unconditionally (React rules), but only the relevant one fetches
  const guideEditData = useGuideEditPage(_experienceId, categoryL1Id, { enabled: isOwner });
  const customerData = useCustomerPage(_experienceId, categoryL1Id, { enabled: !isOwner });
  const { data, loading, error, refetch } = isOwner ? guideEditData : customerData;

  // Use experience details from loader if available, otherwise fetch via hook
  // IMPORTANT: When owner is editing, always use hook data so refetch updates are visible
  const { data: experienceDetailsFromHook, loading: detailsLoading, error: detailsError, refetch: refetchDetails } = useExperienceDetails(_experienceId);
  const experienceDetails = isOwner ? experienceDetailsFromHook : (experienceDetailsFromLoader || experienceDetailsFromHook);

  // Combined refetch function that refetches both section data AND experience details
  const refetchAll = async () => {
    await Promise.all([
      refetch(),
      refetchDetails && refetchDetails()
    ].filter(Boolean));
  };

  const [isMobile, setIsMobile] = useState(false);
  const [isSticky, setIsSticky] = useState(true);
  const [showReserveInHeader, setShowReserveInHeader] = useState(false);
  const [showMobileBookingSheet, setShowMobileBookingSheet] = useState(false);

  // Lifted booking state
  const [selectedDate, setSelectedDate] = useState(null);
  const [guests, setGuests] = useState(2);
  const [showCalendar, setShowCalendar] = useState(false);

  // Review stats state
  const [reviewStats, setReviewStats] = useState(null);

  // SCRUM-1281: Engine-calculated pricing state
  const [enginePrice, setEnginePrice] = useState(null);

  const reviewsRef = useRef(null);
  const bookingCardRef = useRef(null);

  // Use experience details from API/mock
  const listing = experienceDetails ? {
    id: _experienceId,
    guideId: experienceDetails.guideId,
    guideServiceId: _experienceId,
    title: experienceDetails.title,
    rating: experienceDetails.rating,
    reviewCount: experienceDetails.reviewCount,
    location: experienceDetails.location,
    verified: experienceDetails.verified,
    images: experienceDetails.images,
    pricePerPerson: experienceDetails.pricePerPerson,
    serviceFee: experienceDetails.serviceFee,
    guide: experienceDetails.guide,
    // Duration/scheduling fields (SCRUM-828)
    duration_type: experienceDetails.durationType || null,
    duration_days: experienceDetails.durationDays || null,
    prep_days: experienceDetails.prepDays || 0,
    cleanup_days: experienceDetails.cleanupDays || 0,
    start_day_restrictions: experienceDetails.startDayRestrictions || null
  } : null;

  // Use booking hold hook (only if we have listing data)
  const {
    hold,
    loading: holdLoading,
    error: holdError,
    createHold,
    releaseHold,
  } = useBookingHold({
    guideId: listing?.guideId,
    guideServiceId: listing?.guideServiceId
  });

  const breadcrumbItems = [
    { label: 'Home', to: '/' },
    { label: 'Experiences', to: '/' },
    { label: listing?.title || 'Experience' }
  ];

  useEffect(() => {
    window.scrollTo(0, 0);

    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (reviewsRef.current && bookingCardRef.current && !isMobile) {
        const reviewsTop = reviewsRef.current.getBoundingClientRect().top;
        const bookingCardHeight = bookingCardRef.current.offsetHeight;
        const stickyOffset = 96;
        const buffer = 300;

        if (reviewsTop <= bookingCardHeight + stickyOffset + buffer) {
          setIsSticky(false);
          setShowReserveInHeader(true);
        } else {
          setIsSticky(true);
          setShowReserveInHeader(false);
        }
      }
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [isMobile]);

  // SCRUM-1281: Use engine-calculated price when available, fall back to static calculation
  const totalPrice = enginePrice?.totalCents ?? (listing ? (listing.pricePerPerson * guests + listing.serviceFee) : 0);

  // SCRUM-1281: Callback for PricingDisplay — captures engine data for mobile footer / header
  const handlePricingCalculated = useCallback((pricingData) => {
    setEnginePrice(pricingData);
  }, []);

  const handleReserve = async () => {
    if (!selectedDate || !hold) {
      setShowCalendar(true);
      if (isMobile) {
        setShowMobileBookingSheet(true);
      }
      return;
    }

    // SCRUM-1278: No price in URL — BookingFlowPage fetches from server
    const params = new URLSearchParams({
      experienceId: listing.guideServiceId,
      guideId: listing.guideId,
      date: selectedDate,
      guests: guests.toString(),
      title: listing.title,
      minimumAge: (listing.minimumAge || 0).toString(),
    });

    navigate(`/booking/${hold.id}?${params.toString()}`);
  };

  if (loading || detailsLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header onAuthClick={onAuthClick} showSearch={true} />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <SectionSkeleton />
          <SectionSkeleton />
          <SectionSkeleton />
        </div>
        <Footer />
      </div>
    );
  }

  if (error || detailsError) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header onAuthClick={onAuthClick} showSearch={true} />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <ErrorDisplay error={error || detailsError} />
        </div>
        <Footer />
      </div>
    );
  }

  if (!data || !data.sections || data.sections.length === 0 || !listing) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header onAuthClick={onAuthClick} showSearch={true} />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="text-center py-12">
            <p className="text-xl text-gray-600">This experience page is not yet available.</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header
        onAuthClick={onAuthClick}
        showSearch={true}
        showReserveButton={showReserveInHeader}
        reservePrice={totalPrice}
      />
      {/* Breadcrumb hidden */}
      <div className="hidden">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Title and meta info */}
        <div className="mb-4">
          {/* Title hidden, using hero section title instead */}
          <h1 className="text-3xl font-bold text-gray-900 mb-2 hidden">{listing.title}</h1>
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <div className="flex items-center gap-1">
              <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
              {!reviewStats ? (
                <span className="text-gray-400">Loading...</span>
              ) : reviewStats.totalReviews > 0 ? (
                <>
                  <span className="font-semibold">{Number(reviewStats.averageRating).toFixed(2)}</span>
                  <span className="text-gray-600">({reviewStats.totalReviews} {reviewStats.totalReviews === 1 ? 'review' : 'reviews'})</span>
                </>
              ) : (
                <span className="text-gray-600">No reviews yet</span>
              )}
            </div>
            <div className="flex items-center gap-1 text-gray-600">
              <MapPin className="w-4 h-4" />
              <span>{listing.location}</span>
            </div>
            {listing.verified && (
              <div className="flex items-center gap-1 text-rose-600">
                <Award className="w-4 h-4" />
                <span className="font-medium">Verified Guide</span>
              </div>
            )}
            {/* SCRUM-827: Edit in Wizard button for experience owners */}
            {isOwner && (
              <button
                onClick={() => navigate(`/guide/experiences/${_experienceId}/edit`)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-colors"
              >
                <Pencil className="w-3.5 h-3.5" />
                Edit in Wizard
              </button>
            )}
          </div>
        </div>

        {/* Image Gallery */}
        {(() => {
          // Extract hero section data
          const heroSection = data.sections.find(section => section.name === 'hero');
          const heroContentData = heroSection?.contentData || {};

          return (
            <ImageGallery
              images={listing.images}
              alt={listing.title}
              title={listing.title}
              tagline={heroContentData.tagline}
              isGuide={isOwner}
              experienceId={_experienceId}
              sectionTypeId={heroSection?.sectionTypeId}
              heroData={{
                contentData: heroContentData,
                isDraft: heroSection?.isDraft,
                isPublished: heroSection?.isPublished,
                lastEditedAt: heroSection?.lastEditedAt
              }}
              onSectionSaved={refetchAll}
            />
          );
        })()}

        {/* Social Proof - Real-time metrics */}
        <SocialProof experienceId={_experienceId} />

        {/* Main content grid */}
        <div className={isMobile ? '' : 'grid grid-cols-3 gap-8'}>
          {/* Left Column - Dynamic Sections (2/3 width) */}
          <div className={isMobile ? '' : 'col-span-2'}>
            {/* Dynamic Sections from MVP 1.20 - wrapped in card styling like GuideListingPage */}
            {data.sections
              .filter(section =>
                section.name !== 'hero' &&
                section.name !== 'meet_your_guide' &&
                section.name !== 'reviews'
              )
              .map((section) => {
                return (
                  <div key={section.sectionTypeId} className="bg-white rounded-xl shadow-sm p-6 mb-6">
                    <SectionRenderer
                      section={section}
                      isGuide={isOwner}
                      categoryL1Id={categoryL1Id}
                      experienceId={_experienceId}
                      onSectionSaved={refetch}
                      listing={listing}
                    />
                  </div>
                );
              })}
          </div>

          {/* Right Column - Booking Card (1/3 width, Desktop only) */}
          {!isMobile && (
            <div className="col-span-1">
              {/* SCRUM-1281: Engine-calculated pricing display */}
              <PricingDisplay
                experienceId={_experienceId}
                partySize={guests}
                date={selectedDate}
                onPricingCalculated={handlePricingCalculated}
                className="mb-4"
              />
              <BookingCard
                ref={bookingCardRef}
                guideId={listing.guideId}
                guideServiceId={listing.guideServiceId}
                pricePerPerson={enginePrice ? Math.round(enginePrice.baseRateCents / guests) : listing.pricePerPerson}
                serviceFee={enginePrice?.platformFeeCents ?? listing.serviceFee}
                experience={listing}
                isSticky={isSticky}
                selectedDate={selectedDate}
                onSelectedDateChange={setSelectedDate}
                guests={guests}
                onGuestsChange={setGuests}
                showCalendar={showCalendar}
                onShowCalendarChange={setShowCalendar}
                onReserve={handleReserve}
                hold={hold}
                holdLoading={holdLoading}
                holdError={holdError}
                onCreateHold={createHold}
                onReleaseHold={releaseHold}
              />
            </div>
          )}
        </div>

        {/* Guide Card - Full Width */}
        <div className="mt-8">
          <GuideCard guide={listing.guide} />
        </div>

        {/* Reviews Section - Full Width */}
        <div className="mt-8" ref={reviewsRef}>
          <ReviewsSection
            entityType="experience"
            entityId={_experienceId}
            onWriteReview={() => console.log('Write review clicked')}
            onStatsLoaded={setReviewStats}
          />
        </div>

        {/* Cancellation Policy - Guide Owner Only */}
        {isOwner && _experienceId && (
          <div className="mt-8">
            <CancellationPolicySelector experienceId={_experienceId} />
          </div>
        )}

        {/* Service-Specific Info Cards - Full Width */}
        <ServiceInfoRenderer cards={[]} />
      </div>

      {/* Footer */}
      <Footer />

      {/* Mobile Sticky Footer */}
      {isMobile && (
        <>
          <div
            className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-40"
            style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
            role="region"
            aria-label="Booking summary and actions"
          >
            <div className="flex items-center gap-3 px-4 py-3">
              <button
                onClick={() => setShowMobileBookingSheet(true)}
                className="flex-1 text-left active:bg-gray-50 rounded-lg transition-colors py-2 px-3 -mx-3"
                aria-expanded={showMobileBookingSheet}
              >
                <div className="flex items-baseline gap-2 mb-0.5">
                  <span className="text-lg font-bold text-gray-900">{formatCentsToDollars(totalPrice)}</span>
                  <span className="text-sm text-gray-600">total</span>
                </div>
                <div className="text-sm">
                  {!selectedDate ? (
                    <span className="text-red-600 font-semibold">Select dates</span>
                  ) : (
                    <div className="text-gray-600 flex items-center gap-1.5">
                      <span>{selectedDate}</span>
                      <span>·</span>
                      <span>{guests} {guests === 1 ? 'guest' : 'guests'}</span>
                    </div>
                  )}
                </div>
              </button>

              <button
                onClick={handleReserve}
                disabled={!selectedDate || !hold || holdLoading}
                className={`${
                  !selectedDate || !hold || holdLoading
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : theme.buttonPrimary
                } font-semibold py-3 px-6 rounded-lg transition shadow-md whitespace-nowrap flex-shrink-0`}
              >
                {holdLoading ? 'Processing...' : 'Reserve'}
              </button>
            </div>
          </div>

          {/* Mobile Booking Sheet */}
          <BottomSheet
            isOpen={showMobileBookingSheet}
            onClose={() => {
              setShowMobileBookingSheet(false);
              setShowCalendar(false);
            }}
            title="Book this experience"
            initialHeight={60}
            expandedHeight={92}
          >
            {/* SCRUM-1281: Engine pricing in mobile booking sheet */}
            <PricingDisplay
              experienceId={_experienceId}
              partySize={guests}
              date={selectedDate}
              onPricingCalculated={handlePricingCalculated}
              className="mb-4"
            />
            <BookingCard
              ref={bookingCardRef}
              guideId={listing.guideId}
              guideServiceId={listing.guideServiceId}
              pricePerPerson={enginePrice ? Math.round(enginePrice.baseRateCents / guests) : listing.pricePerPerson}
              serviceFee={enginePrice?.platformFeeCents ?? listing.serviceFee}
              experience={listing}
              isSticky={false}
              selectedDate={selectedDate}
              onSelectedDateChange={setSelectedDate}
              guests={guests}
              onGuestsChange={setGuests}
              showCalendar={showCalendar}
              onShowCalendarChange={setShowCalendar}
              onReserve={handleReserve}
              hold={hold}
              holdLoading={holdLoading}
              holdError={holdError}
              onCreateHold={createHold}
              onReleaseHold={releaseHold}
            />
          </BottomSheet>
        </>
      )}
    </div>
  );
}

/**
 * Main Experience Page Layout Component
 */
export default function ExperiencePageLayout({
  experienceId,
  categoryL1Id,
  mode: _mode = 'customer',
  onAuthClick
}) {
  // Note: Guide edit mode has been removed. This component only renders customer view.
  // The CustomerExperiencePage component handles both customer and guide editing modes internally.
  return (
    <CustomerExperiencePage
      experienceId={experienceId}
      categoryL1Id={categoryL1Id}
      onAuthClick={onAuthClick}
    />
  );
}
